import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Box, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableRow, Typography, useMediaQuery, useTheme } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "../../content/pages/homepage.module.css";
import { WidgetContext } from "src/contexts/WidgetContext";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LoaderFacts from "../Loader/Loader";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { RankingsFilterValue } from "./AllRankingsFilterToggle";
import PositionsButtonsRankings from "../Rankings/PositionsButtonsRankings";
import TextFieldRankings from "../TextField/TextFieldRankings";
import ButtonsScrollUp from "../ButtonsSections/ButtonScrollUp";
import PlayerDetail from "../PlayerDetail/PlayerDetail";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

/** When multiple players share the same numeric rank in the current view, show e.g. "2T" instead of "2". */
function formatRankDisplay(playerRank: number | string | null | undefined, tieCounts: Map<number, number>): string {
  if (playerRank == null) return "-";
  if (typeof playerRank === "string") return playerRank;
  if (playerRank === 0) return "-";
  const n = tieCounts.get(playerRank) ?? 0;
  if (n > 1) return `${playerRank}T`;
  return String(playerRank);
}

function AllRankingsResponse({ onClose, allRankingsData, loadingAllRankings }) {

  // Map to use same variable names internally
  const rankingsData = allRankingsData;
  const loadingRankings = loadingAllRankings;
  const [selectedPosition, setSelectedPosition] = useState("ALL");
  const [selectedTimeframe, setSelectedTimeframe] = useState("Overall");
  const [rankingsFilter, setRankingsFilter] = useState<RankingsFilterValue>("all");

  // Derive flat players array from the grouped structure { current: [...], rookie: [...], draft_prospect: [...] }
  const playersObj = rankingsData?.players;
  const currentPlayers = useMemo(() => (Array.isArray(playersObj?.current) ? playersObj.current : []), [playersObj]);
  const rookiePlayers = useMemo(() => (Array.isArray(playersObj?.rookie) ? playersObj.rookie : []), [playersObj]);
  const draftProspectPlayers = useMemo(() => (Array.isArray(playersObj?.draft_prospect) ? playersObj.draft_prospect : []), [playersObj]);

  const availableRankingsFilters = useMemo<RankingsFilterValue[]>(() => {
    const available: RankingsFilterValue[] = [];
    if (currentPlayers.length > 0) available.push("current");
    if (rookiePlayers.length > 0) available.push("rookies");
    if (draftProspectPlayers.length > 0) available.push("draft_prospects");
    return available; 
  }, [currentPlayers, rookiePlayers, draftProspectPlayers]);

  const activePlayers = useMemo(() => {
    if (rankingsFilter === "current") return currentPlayers;
    if (rankingsFilter === "rookies") return rookiePlayers;
    if (rankingsFilter === "draft_prospects") return draftProspectPlayers;
    return [...currentPlayers, ...rookiePlayers, ...draftProspectPlayers];
  }, [currentPlayers, rookiePlayers, draftProspectPlayers, rankingsFilter]);

  useEffect(() => {
    if (rankingsFilter !== "all" && !availableRankingsFilters.includes(rankingsFilter)) {
      setRankingsFilter("all");
    }
  }, [availableRankingsFilters, rankingsFilter]);

  const columns = useMemo(
    () =>
      [
        { id: "rank", label: "RANK" },
        { id: "name", label: selectedPosition === "DEF" || selectedPosition === "DST" ? "TEAM" : "PLAYER" },
        { id: "age", label: "AGE" },
        { id: "pos", label: "POS" },
        { id: "team", label: "TEAM" },
        { id: "points", label: "FP" },
        { id: "value", label: selectedTimeframe === "Week" ? "PROJ" : selectedTimeframe === "FP + ROS" ? "ROS" : "VALUE" },
        // { id: "value", label: selectedTimeframe === "Week" || selectedTimeframe === "FP + ROS" ? "ROS" : "VALUE" },
      ]
        .filter((col) => (selectedPosition === "DEF" || selectedPosition === "DST" ? col.id !== "age" && col.id !== "team" : true))
        .filter((col) => !(selectedTimeframe === "Overall" && col.id === "value"))
        .filter((col) => !(selectedTimeframe === "Overall" && col.id === "points")),
    [selectedPosition, selectedTimeframe]
  );

  const isPreseason = Boolean(rankingsData?.is_preseason);
  const NFL_REGULAR_SEASON_WEEKS = 17;

  // importing the team name from context
  const { selectedPartner, setSelectedPartner } = useContext(WidgetContext);
  const containerRef = useRef(null);
  const positions = ["ALL", "QB", "RB", "WR", "TE"];
  const currentPositionIndex = positions.indexOf(selectedPosition);
  const [numPlayersToShow, setNumPlayersToShow] = useState(150);
  const [showLoader, setShowLoader] = useState(false);
  const loadMoreRef = useRef(null);
  // State for search text
  const [searchText, setSearchText] = useState("");
  const [expandedPlayer, setExpandedPlayer] = useState(null);

  const [isPlayerDrawerOpen, setIsPlayerDrawerOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const availablePositions = useMemo(() => {
    if (!activePlayers.length) {
      return ["ALL", "QB", "RB", "WR", "TE"];
    }
    const positions = [...new Set(activePlayers.map((p) => p.position))];
    const sortOrder = ["QB", "RB", "WR", "TE", "K", "DEF", "DST"];
    positions.sort((a, b) => {
      const indexA = sortOrder.indexOf(a as string);
      const indexB = sortOrder.indexOf(b as string);
      if (indexA === -1 && indexB === -1) return (a as string).localeCompare(b as string);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
    return ["ALL", ...positions];
  }, [activePlayers]);

  const hasKickers = useMemo(() => activePlayers.some((p) => p.position === "K"), [activePlayers]);
  const hasDefenses = useMemo(() => activePlayers.some((p) => p.position === "DEF"), [activePlayers]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (rankingsData?.rank_type !== "current_week_fp" && selectedTimeframe !== "Overall") {
      setSelectedTimeframe("Overall");
    }
  }, [rankingsData?.rank_type, selectedTimeframe]);

  // Sorting states
  const [sortDirection, setSortDirection] = useState("asc");
  // Sorting column
  const [sortColumn, setSortColumn] = useState(null);

  const getPlayerRank = useCallback((player) => {
    const isAllPositions = selectedPosition === "ALL";
    const isRookieFilter = rankingsFilter === "rookies";
    const isDraftProspectFilter = rankingsFilter === "draft_prospects";
    const isCurrentFilter = rankingsFilter === "current";

    if (selectedTimeframe === "Week" && rankingsData?.rank_type === "current_week_fp") {
      if (isPreseason) {
        if (isRookieFilter) {
          return isAllPositions ? player.rookie_ros_value_overall_rank : player.rookie_ros_value_positional_rank;
        }
        if (isDraftProspectFilter) {
          return isAllPositions ? player.draft_prospect_ros_value_overall_rank : player.draft_prospect_ros_value_positional_rank;
        }
        if (isCurrentFilter) {
          return isAllPositions
            ? (player.veteran_ros_value_overall_rank ?? player.ros_value_overall_rank)
            : (player.veteran_ros_value_positional_rank ?? player.ros_value_positional_rank);
        }
        return isAllPositions ? player.ros_value_overall_rank : player.ros_value_positional_rank;
      }
      if (isRookieFilter) {
        return isAllPositions ? player.rookie_current_week_fp_proj_overall_rank : player.rookie_current_week_fp_proj_positional_rank;
      }
      if (isDraftProspectFilter) {
        return isAllPositions ? player.draft_prospect_current_week_fp_proj_overall_rank : player.draft_prospect_current_week_fp_proj_positional_rank;
      }
      if (isCurrentFilter) {
        return isAllPositions
          ? (player.veteran_current_week_fp_proj_overall_rank ?? player.current_week_fp_proj_overall_rank)
          : (player.veteran_current_week_fp_proj_positional_rank ?? player.current_week_fp_proj_positional_rank);
      }
      return isAllPositions ? player.current_week_fp_proj_overall_rank : player.current_week_fp_proj_positional_rank;
    }

    if (selectedTimeframe === "FP + ROS" && rankingsData?.rank_type === "current_week_fp") {
      if (isRookieFilter) {
        return isAllPositions ? player.rookie_ros_value_overall_rank : player.rookie_ros_value_positional_rank;
      }
      if (isDraftProspectFilter) {
        return isAllPositions ? player.draft_prospect_ros_value_overall_rank : player.draft_prospect_ros_value_positional_rank;
      }
      if (isCurrentFilter) {
        return isAllPositions
          ? (player.veteran_ros_value_overall_rank ?? player.ros_value_overall_rank)
          : (player.veteran_ros_value_positional_rank ?? player.ros_value_positional_rank);
      }
      return isAllPositions ? player.ros_value_overall_rank : player.ros_value_positional_rank;
    }

    if (selectedTimeframe === "Overall" || rankingsData?.rank_type !== "current_week_fp") {
      if (isRookieFilter) {
        return isAllPositions ? player.rookie_rank : player.rookie_position_rank;
      }
      if (isDraftProspectFilter) {
        return isAllPositions ? player.draft_prospect_rank : player.draft_prospect_position_rank;
      }
      if (isCurrentFilter) {
        return isAllPositions
          ? (player.veteran_rank ?? player.rank)
          : (player.veteran_position_rank ?? player.sub_rank);
      }
      return isAllPositions ? player.rank : player.sub_rank;
    }

    return isAllPositions ? player.rank : player.sub_rank;
  }, [isPreseason, rankingsData?.rank_type, rankingsFilter, selectedPosition, selectedTimeframe]);

  //New ref for the scrollable content
  const scrollableRef = useRef(null);

  // Function to get the correct value for the player
  const getPlayerValue = (player) => {
    // If the season is not in season, return the player_value
    // if (!rankingsData?.in_season) {
    //   return player.player_value ?? 0;
    // }
    // If rank_type is "player_value", return the player_value
    if (rankingsData?.rank_type === "player_value") {
      return player.player_value ?? 0;
    }
    // If the timeframe is FP + ROS, return the ros_value
    if (selectedTimeframe === "FP + ROS") {
      return player.ros_value ?? 0;
    }
    // If the timeframe is Week, return weekly proj (or ROS/17 PPG in preseason)
    if (selectedTimeframe === "Week") {
      if (isPreseason) {
        const rosValue = player.ros_value;
        if (rosValue == null) return 0;
        return Math.round((Number(rosValue) / NFL_REGULAR_SEASON_WEEKS) * 100) / 100;
      }
      return player.current_week_fp_proj ?? 0;
    }
    if (selectedTimeframe === "Overall") {
      return player.player_value ?? 0;
    }
    return player.player_value ?? 0; // Default to player_value
  };

  const handleRowClick = (playerKey) => {
    setExpandedPlayer(expandedPlayer === playerKey ? null : playerKey);
  };

  const goBack = () => {
    setSelectedPartner(null);
  };

  const handlePositionSelect = (positionOrTimeframe) => {
    if (["Week", "FP + ROS", "Overall"].includes(positionOrTimeframe)) {
      setSelectedTimeframe(positionOrTimeframe);
      // When timeframe changes, reset position to ALL
      // setSelectedPosition("ALL");
    } else {
      setSelectedPosition(positionOrTimeframe);
    }
    // Reset the column sort whenever the main filter changes
    setSortColumn(null);
    setSortDirection("asc");
    setNumPlayersToShow(150); // Reset player count on filter change
  };

  const scrollToTop = () => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const sortedAndFilteredPlayers = useMemo(() => {
    if (!activePlayers.length) {
      return [];
    }

    return (
      activePlayers
        .filter((player) => {
          if (selectedPosition === "ALL") {
            return true;
          }
          return player.position === selectedPosition;
        })
        .filter((player) => player.name.toLowerCase().includes(searchText.toLowerCase()))
        // Filter out players with position "K"
        // .filter((player) => player.position !== "K")
        // Sorting players by points or value
        .sort((a, b) => {
          // if sortColumn is points, sort by points value
          if (sortColumn === "points") {
            // if sortDirection is asc, sort by points ascending. If sortDirection is desc, sort by points descending
            return sortDirection === "asc" ? a.player_fantasy_points - b.player_fantasy_points : b.player_fantasy_points - a.player_fantasy_points;
          }
          if (sortColumn === "value") {
            const valueA = getPlayerValue(a);
            const valueB = getPlayerValue(b);
            return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
          }

          // Sorting by age
          if (sortColumn === "age") {
            const ageA = a.age;
            const ageB = b.age;
            const aIsInvalid = ageA == null || ageA === 0;
            const bIsInvalid = ageB == null || ageB === 0;
            if (aIsInvalid && bIsInvalid) return 0;
            if (aIsInvalid) return 1;
            if (bIsInvalid) return -1;
            return sortDirection === "asc" ? ageB - ageA : ageA - ageB;
          }

          const isAllPositions = selectedPosition === "ALL";

          const resolveSortRank = (player) => getPlayerRank(player);

          if (selectedTimeframe === "Overall") {
            if (isAllPositions) {
              const aRank = resolveSortRank(a);
              const bRank = resolveSortRank(b);
              const aIsInvalid = typeof aRank === "string" || aRank == null || aRank === 0;
              const bIsInvalid = typeof bRank === "string" || bRank == null || bRank === 0;
              if (aIsInvalid && bIsInvalid) return 0;
              if (aIsInvalid) return 1;
              if (bIsInvalid) return -1;
              return aRank - bRank;
            } else {
              const aRank = resolveSortRank(a);
              const bRank = resolveSortRank(b);
              const aIsInvalid = typeof aRank === "string" || aRank == null || aRank === 0;
              const bIsInvalid = typeof bRank === "string" || bRank == null || bRank === 0;
              if (aIsInvalid && bIsInvalid) return 0;
              if (aIsInvalid) return 1;
              if (bIsInvalid) return -1;
              return aRank - bRank;
            }
          }

          if (selectedTimeframe === "Week" && rankingsData?.rank_type === "current_week_fp") {
            if (isPreseason) {
              if (isAllPositions) {
                const aRank = a.ros_value_overall_rank;
                const bRank = b.ros_value_overall_rank;
                const aIsInvalid = typeof aRank === "string" || aRank == null || aRank === 0;
                const bIsInvalid = typeof bRank === "string" || bRank == null || bRank === 0;
                if (aIsInvalid && bIsInvalid) return 0;
                if (aIsInvalid) return 1;
                if (bIsInvalid) return -1;
                return aRank - bRank;
              } else {
                const aRank = a.ros_value_positional_rank;
                const bRank = b.ros_value_positional_rank;
                const aIsInvalid = typeof aRank === "string" || aRank == null || aRank === 0;
                const bIsInvalid = typeof bRank === "string" || bRank == null || bRank === 0;
                if (aIsInvalid && bIsInvalid) return 0;
                if (aIsInvalid) return 1;
                if (bIsInvalid) return -1;
                return aRank - bRank;
              }
            }
            if (isAllPositions) {
              const aRank = a.current_week_fp_proj_overall_rank;
              const bRank = b.current_week_fp_proj_overall_rank;
              const aIsInvalid = typeof aRank === "string" || aRank == null || aRank === 0;
              const bIsInvalid = typeof bRank === "string" || bRank == null || bRank === 0;
              if (aIsInvalid && bIsInvalid) return 0;
              if (aIsInvalid) return 1;
              if (bIsInvalid) return -1;
              return aRank - bRank;
            } else {
              const aRank = a.current_week_fp_proj_positional_rank;
              const bRank = b.current_week_fp_proj_positional_rank;
              const aIsInvalid = typeof aRank === "string" || aRank == null || aRank === 0;
              const bIsInvalid = typeof bRank === "string" || bRank == null || bRank === 0;
              if (aIsInvalid && bIsInvalid) return 0; // Ambos inválidos: mantiene orden
              if (aIsInvalid) return 1; // A es inválido, mueve al final
              if (bIsInvalid) return -1; // B es inválido, mueve al final
              return aRank - bRank; // Ordena normalmente si ambos son válidos
            }
          }

          if (selectedTimeframe === "FP + ROS" && rankingsData?.rank_type === "current_week_fp") {
            if (isAllPositions) {
              const aRank = a.ros_value_overall_rank;
              const bRank = b.ros_value_overall_rank;
              const aIsInvalid = typeof aRank === "string" || aRank == null || aRank === 0;
              const bIsInvalid = typeof bRank === "string" || bRank == null || bRank === 0;
              if (aIsInvalid && bIsInvalid) return 0;
              if (aIsInvalid) return 1;
              if (bIsInvalid) return -1;
              return aRank - bRank;
            } else {
              const aRank = a.ros_value_positional_rank;
              const bRank = b.ros_value_positional_rank;
              const aIsInvalid = typeof aRank === "string" || aRank == null || aRank === 0;
              const bIsInvalid = typeof bRank === "string" || bRank == null || bRank === 0;
              if (aIsInvalid && bIsInvalid) return 0;
              if (aIsInvalid) return 1;
              if (bIsInvalid) return -1;
              return aRank - bRank;
            }
          }

          if (rankingsData?.rank_type !== "current_week_fp") {
            if (isAllPositions) {
              const aRank = resolveSortRank(a);
              const bRank = resolveSortRank(b);
              const aIsInvalid = typeof aRank === "string" || aRank == null || aRank === 0;
              const bIsInvalid = typeof bRank === "string" || bRank == null || bRank === 0;
              if (aIsInvalid && bIsInvalid) return 0;
              if (aIsInvalid) return 1;
              if (bIsInvalid) return -1;
              return aRank - bRank;
            } else {
              const aRank = resolveSortRank(a);
              const bRank = resolveSortRank(b);
              const aIsInvalid = typeof aRank === "string" || aRank == null || aRank === 0;
              const bIsInvalid = typeof bRank === "string" || bRank == null || bRank === 0;
              if (aIsInvalid && bIsInvalid) return 0;
              if (aIsInvalid) return 1;
              if (bIsInvalid) return -1;
              return aRank - bRank;
            }
          }

          return 0;
        })
    );
  }, [activePlayers, isPreseason, rankingsData, rankingsFilter, selectedPosition, searchText, sortColumn, sortDirection, selectedTimeframe, getPlayerRank]);

  const loadedPlayers = useMemo(() => sortedAndFilteredPlayers.slice(0, numPlayersToShow), [sortedAndFilteredPlayers, numPlayersToShow]);

  // In this useEffect an observer is used to detect when load more players
  useEffect(() => {
    if (!sortedAndFilteredPlayers) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !loadingRankings && !showLoader && loadedPlayers.length < sortedAndFilteredPlayers.length) {
          setShowLoader(true);
          setTimeout(() => {
            const newCount = loadedPlayers.length + 150;
            setNumPlayersToShow(newCount > sortedAndFilteredPlayers.length ? sortedAndFilteredPlayers.length : newCount);
            setShowLoader(false);
          }, 2000); // Fakes load after 2 seconds
        }
      },
      {
        root: null,
        threshold: 1.0,
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [loadedPlayers, sortedAndFilteredPlayers, loadingRankings, showLoader]);

  // const sortPlayersByPoints = (players) => {
  //   return players.sort((a, b) => b.player_fantasy_points - a.player_fantasy_points);
  // };

  // Function to handle sorting in column points
  const handleSort = (columnId) => {
    if (columnId === sortColumn) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  //const filteredLoadedPlayers = sortPlayersByPoints(
  //   loadedPlayers
  //     .filter((player) => {
  //       if (selectedPosition === "ALL" || !selectedPosition) {
  //         return true;
  //       } else {
  //         return player.position === selectedPosition;
  //       }
  //     })
  //     .filter((player) => player.name.toLowerCase().includes(searchText.toLowerCase()))
  // );

  // Filtering players by position and search text
  const filteredLoadedPlayers = loadedPlayers;

  // Function to separate player's full name in 2 rows, and if it has more than 15 characters, add ...
  const getLastNameWithSuspensivePoints = (fullName) => {
    const firstName = fullName.split(" ")[0];
    const lastName = fullName.split(" ").slice(1).join(" ");
    if (fullName.length < 16) {
      return lastName; // Return full last name if fullName is exactly 16 characters
    }
    return fullName.length > 16 ? `${lastName.substring(0, 15 - firstName.length)}...` : lastName;
  };

  // Function to get the correct rank based on selected filters (player type + position).
  // See getPlayerRank defined above (used for sort + display).

  // How many players in the current filtered/sorted list share each numeric rank (for "2T" tie suffix).
  const rankTieCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (const player of sortedAndFilteredPlayers) {
      const r = getPlayerRank(player);
      if (typeof r === "number" && r != null && !Number.isNaN(r) && r !== 0) {
        counts.set(r, (counts.get(r) ?? 0) + 1);
      }
    }
    return counts;
  }, [sortedAndFilteredPlayers, getPlayerRank]);

  // Function to format decimal to 1 decimal place and remove .0 if it is needed
  const formatDecimal = (value) => {
    if (value == null) return null;
    // Truncate instead of round: multiply by 10, truncate, divide by 10
    const truncated = Math.floor(value * 10) / 10;
    // Format with 1 decimal and remove .0 if needed
    return truncated.toFixed(1).replace(/\.0$/, "");
  };

  // Function to handle filter change
  const onFilterChange = (filter) => {
    setRankingsFilter(filter);
    // setSelectedPosition("ALL"); // I commented this because when the filter changes (filter toggle button), the position should not be reset to ALL I think
    setSortColumn(null);
    setSortDirection("asc");
    setNumPlayersToShow(150);
    scrollToTop();
  };

  return (
    <Box ref={containerRef}>
      {loadingRankings ? (
        <LoaderFacts onClose={onClose} goBack={goBack} />
      ) : (
        // Wrapper container with absolute position
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            backgroundColor: "black",
            overflow: "hidden",
            WebkitOverflowScrolling: "touch",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          {/* Header container */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              backgroundColor: "black",
            }}
          >
            <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" p={1} pb={1}>
              <IconButton aria-label="close" onClick={onClose}>
                <ArrowBackIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }} />
              </IconButton>
              <Typography variant="h4" color={`${PureLightThemeCaaS.colors.alpha.white[100]}`} textAlign={"center"} p={1} className={styles.tradeTeam}>
                {selectedPartner} Rankings
              </Typography>
              <IconButton aria-label="close" onClick={onClose}>
                <CloseIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }} />
              </IconButton>
            </Box>
            <>
              <PositionsButtonsRankings
                onSelectPosition={handlePositionSelect}
                selectedPosition={selectedPosition}
                selectedTimeframe={selectedTimeframe}
                scrollToTop={scrollToTop}
                currentWeek={rankingsData?.current_week}
                isSeason={rankingsData?.in_season}
                rankType={rankingsData?.rank_type}
                availablePositions={availablePositions}
                selectedFilter={rankingsFilter}
                onFilterChange={onFilterChange}
                availableRankingsFilters={availableRankingsFilters}
                isPreseason={isPreseason}
              />
              {/* <AllRankingsFilterToggle
                selectedFilter={rankingsFilter}
                onFilterChange={(filter) => {
                  setRankingsFilter(filter);
                  setSelectedPosition("ALL");
                  setSortColumn(null);
                  setSortDirection("asc");
                  setNumPlayersToShow(150);
                  scrollToTop();
                }}
              /> */}
              <TextFieldRankings searchText={searchText} setSearchText={setSearchText} />
            </>
          </Box>

          {/* Scrollable content - add ref here */}
          <Box
            ref={scrollableRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              scrollbarWidth: "none",
            }}
          >
            <Box sx={{ maxHeight: "auto", overflowY: "auto", p: 1, pb: 4, pt: 0 }}>
              <TableContainer sx={{ overflow: "hidden", pt: 0 }}>
                <Table sx={{ bgcolor: `${PureLightThemeCaaS.colors.alpha.black[100]}` }}>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        // Sorting column points and value
                        // onClick={() => (column.id === "points" || column.id === "value") && handleSort(column.id)}
                        // Sorting column points and value (disabled on Overall timeframe)
                        onClick={() => {
                          if (column.id === "age") handleSort(column.id);
                          else if ((column.id === "points" || column.id === "value") && selectedTimeframe !== "Overall") handleSort(column.id);
                        }}
                        sx={{
                          borderColor: "none",
                          padding: column.id === "name" ? "5 5 10 10" : "5 5 10 5",
                          fontSize: "10px",
                          color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                          borderBottom: "0px",
                          textAlign: column.id === "name" ? "left" : "center",
                          marginBottom: "15px",
                          // cursor: column.id === "points" || column.id === "value" ? "pointer" : "default",
                          cursor:
                            column.id === "age" || ((column.id === "points" || column.id === "value") && selectedTimeframe !== "Overall")
                              ? "pointer"
                              : "default",
                        }}
                        className={styles.dataTableTitle}
                      >
                        {column.id === "points" || column.id === "value" ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {column.label}
                            {selectedTimeframe !== "Overall" &&
                              ((sortColumn === column.id ? sortDirection : "desc") === "asc" ? (
                                <ArrowDropUpIcon sx={{ fontSize: isMobile ? "18px" : "20px" }} />
                              ) : (
                                <ArrowDropDownIcon sx={{ fontSize: isMobile ? "18px" : "20px" }} />
                              ))}
                            {/* {selectedTimeframe !== "Overall" && sortColumn === column.id ? (
                              sortDirection === "asc" ? (
                                <ArrowDropUpIcon sx={{ fontSize: "18px" }} />
                              ) : (
                                <ArrowDropDownIcon sx={{ fontSize: "18px" }} />
                              )
                            ) : null} */}
                          </Box>
                        ) : column.id === "age" ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {column.label}
                            {(sortColumn === "age" ? sortDirection : "desc") === "asc" ? (
                              <ArrowDropUpIcon sx={{ fontSize: isMobile ? "18px" : "20px" }} />
                            ) : (
                              <ArrowDropDownIcon sx={{ fontSize: isMobile ? "18px" : "20px" }} />
                            )}
                          </Box>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>

                  <TableBody className={styles.spacingRow}>
                    {filteredLoadedPlayers.map((player, index) => {
                      const playerRank = getPlayerRank(player);
                      // Grey out name/stats only on the Week X tab when the player already played
                      const rowTextColor =
                        selectedTimeframe === "Week" && player.has_played === true
                          ? PureLightThemeCaaS.colors.alpha.lightGray[50]
                          : PureLightThemeCaaS.colors.alpha.white[100];
                      return (
                        <React.Fragment key={`${player.key}-${index}`}>
                          <TableRow
                            key={`${player.key}-${index}`}
                            className={styles.spacingRow}
                            style={{
                              borderRight: "2px solid #000000",
                              height: "40px",
                              // width: "100%",
                              borderTopLeftRadius: "10px",
                              marginBottom: "5px",
                              backgroundColor: "#000000",
                            }}
                            sx={{
                              "&:focus": {
                                backgroundColor: "yellow",
                              },
                              "&.Mui-focusVisible": {
                                backgroundColor: "yellow",
                              },
                              // cursor: "pointer", // DISCOMMENTED THIS WHEN WE WANT TO USE THE PLAYER DETAIL COMPONENT
                            }}
                            // onClick={() => handleRowClick(`${player.key}-${index}`)}
                            onClick={() => {
                              setSelectedPlayer(player);
                              setIsPlayerDrawerOpen(true);
                            }}
                          >
                            <TableCell
                              sx={{
                                p: 0,
                                m: 0,
                                width: "10%",

                                borderBottom: "1px solid #999999",
                              }}
                              className={styles.dataTable}
                            >
                              <Box
                                sx={{
                                  margin: "auto",
                                  bgcolor: `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                                  borderRadius: "10px",
                                  p: 0.8,
                                  fontSize: "14px",
                                  textAlign: "center",
                                  color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                                }}
                              >
                                {/* Display the rank of the player but allways in secuence order */}
                                {/* {player.displayRank} */}
                                {/*  {getPlayerRank(player)} */}
                                {/* {searchText ? getPlayerRank(player) : player.displayRank} */}
                                {/* {typeof playerRank === "string" ? playerRank : player.displayRank} */}
                                {/* {searchText ? (typeof playerRank === "string" ? playerRank : playerRank) : typeof playerRank === "string" ? playerRank : player.displayRank} */}
                                {/* {searchText
                                  ? playerRank == null
                                    ? "-"
                                    : typeof playerRank === "string"
                                    ? playerRank
                                    : playerRank
                                  : typeof playerRank === "string"
                                  ? playerRank
                                  : player.displayRank} */}
                                {formatRankDisplay(playerRank, rankTieCounts)}
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                width: "25%",
                                p: 0.2,
                                pl: 1,
                                fontSize: "14px",
                                color: rowTextColor,
                                borderBottom: "1px solid #999999",
                              }}
                              className={styles.dataTable}
                            >
                              {isMobile ? (
                                <Box>
                                  <Typography
                                    sx={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 1, // Limite text to 1 line
                                      WebkitBoxOrient: "vertical", // Define the orientation of the text
                                      overflow: "hidden", // Hide overflow
                                      textOverflow: "ellipsis", // Add ellipsis (... at the end of the text when the text is too long)
                                      lineHeight: "1.2em", // Set line height
                                      maxHeight: "1.2em", // Set max height
                                      color: rowTextColor,
                                    }}
                                  >
                                    {player.name.split(" ")[0]}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 1, // Limite text to 1 line
                                      WebkitBoxOrient: "vertical", // Define the orientation of the text
                                      overflow: "hidden", // Hide overflow
                                      textOverflow: "ellipsis", // Add ellipsis (... at the end of the text when the text is too long)
                                      lineHeight: "1.2em", // Set line height
                                      maxHeight: "1.2em", // Set max height
                                      color: rowTextColor,
                                    }}
                                  >
                                    {player.name.split(" ").slice(1).join(" ")}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography sx={{ color: rowTextColor }}>{player.name}</Typography>
                              )}
                            </TableCell>
                            {selectedPosition !== "DEF" && selectedPosition !== "DST" && (
                              <TableCell
                                sx={{
                                  p: 0,
                                  fontSize: "14px",
                                  textAlign: "center",
                                  color: rowTextColor,
                                  borderBottom: "1px solid #999999",
                                  width: "10%",
                                }}
                                className={styles.dataTable}
                              >
                                {player.age}
                              </TableCell>
                            )}
                            <TableCell
                              sx={{
                                p: 0,
                                fontSize: "14px",
                                textAlign: "center",
                                color: rowTextColor,
                                borderBottom: "1px solid #999999",
                                width: "10%",
                              }}
                              className={styles.dataTable}
                            >
                              {player.position}
                            </TableCell>
                            {selectedPosition !== "DEF" && selectedPosition !== "DST" && (
                              <TableCell
                                sx={{
                                  p: 0,
                                  fontSize: "14px",
                                  textAlign: "center",
                                  color: rowTextColor,
                                  borderBottom: "1px solid #999999",
                                  width: "10%",
                                }}
                                className={styles.dataTable}
                              >
                                {player.team}
                              </TableCell>
                            )}
                            {selectedTimeframe !== "Overall" && (
                              <TableCell
                                sx={{
                                  p: 0,
                                  fontSize: "14px",
                                  // fontWeight: "bold",
                                  textAlign: "center",
                                  color: rowTextColor,
                                  borderBottom: "1px solid #999999",
                                  width: "10%",
                                }}
                                className={styles.dataTable}
                              >
                                {/* No decimal points*/}
                                {/* {Math.round(player.player_fantasy_points)} */}
                                {/* {player.player_fantasy_points?.toFixed(1).replace(/\.0$/, "")} */}
                                {formatDecimal(player?.player_fantasy_points)}
                              </TableCell>
                            )}
                            {/* Only show the value cell if the timeframe is not Overall */}
                            {selectedTimeframe !== "Overall" && (
                              <TableCell
                                sx={{
                                  p: 0,
                                  fontSize: "14px",
                                  // fontWeight: "bold",
                                  textAlign: "center",
                                  color: rowTextColor,
                                  borderBottom: "1px solid #999999",
                                  width: "10%",
                                }}
                                className={styles.dataTable}
                              >
                                {/* {Math.round(getPlayerValue(player))} */}
                                {/* {getPlayerValue(player)?.toFixed(1).replace(/\.0$/, "")} */}
                                {formatDecimal(getPlayerValue(player))}
                              </TableCell>
                            )}
                          </TableRow>

                          {/* PLAYERS EXPANDED WHIT ADITIONAL DATA */}
                          {/* {expandedPlayer === `${player.key}-${index}` && (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              sx={{
                                textAlign: "center",
                                padding: 0.5,
                                borderBottom: "none",
                                color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  justifyContent: "space-between",
                                  alignContent: "center",
                                  margin: "auto",
                                }}
                              >
                                <Typography sx={{ fontSize: "12px", color: "#ffffff", textAlign: "center" }}>{player.fantasy_team}</Typography>
                                <Typography sx={{ fontSize: "12px", color: "#ffffff", textAlign: "center" }}>{player.player_value}</Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  justifyContent: "space-between",
                                  alignContent: "center",
                                }}
                              >
                                <Typography sx={{ fontSize: "10px", color: "#999999", textAlign: "center" }}>OWNED BY</Typography>
                                <Typography sx={{ fontSize: "10px", color: "#999999", textAlign: "center" }}>VALUE</Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )} */}
                        </React.Fragment>
                      );
                    })}
                    {showLoader && (
                      <TableRow>
                        <TableCell colSpan={7} style={{ textAlign: "center" }}>
                          <CircularProgress size={25} sx={{ color: "#FF672D" }} />
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow ref={loadMoreRef}>
                      <TableCell style={{ height: "1px" }} colSpan={5} />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>

          {/* Button to go to top */}
          <ButtonsScrollUp scrollToTop={scrollToTop} />

          {/* FOR NOW IN V1, WE ARE NOT SHOWING THE PLAYER DETAIL SWIPEABLE DRAWER */}

          {/* Swipeable Drawer for Player Detail */}
          {/* <PlayerDetail selectedPlayer={selectedPlayer} isPlayerDrawerOpen={isPlayerDrawerOpen} setIsPlayerDrawerOpen={setIsPlayerDrawerOpen} /> */}
        </Box>
      )}
    </Box>
  );
}

export default AllRankingsResponse;
